import { useState, useEffect } from 'react';
import axios from 'axios';
import SupervisorLayout from '../../components/layout/SupervisorLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Clock,
  ArrowUpRight,
  ArrowDownRight,
  Printer,
  FileArchive,
  User,
  Eye,
  MapPin,
  Calendar,
  Briefcase
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { formatUK } from '../../lib/ukTime';
import { toast } from 'sonner';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MONTHS = [
  { value: '01', label: 'January' },
  { value: '02', label: 'February' },
  { value: '03', label: 'March' },
  { value: '04', label: 'April' },
  { value: '05', label: 'May' },
  { value: '06', label: 'June' },
  { value: '07', label: 'July' },
  { value: '08', label: 'August' },
  { value: '09', label: 'September' },
  { value: '10', label: 'October' },
  { value: '11', label: 'November' },
  { value: '12', label: 'December' },
];

export default function SupervisorClockHistoryPage() {
  const [employees, setEmployees] = useState([]);
  const [loadingEmployees, setLoadingEmployees] = useState(true);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState('');
  const [selectedYear, setSelectedYear] = useState(String(new Date().getFullYear()));
  const [selectedMonth, setSelectedMonth] = useState(String(new Date().getMonth() + 1).padStart(2, '0'));
  const [viewData, setViewData] = useState(null);
  const [loadingView, setLoadingView] = useState(false);
  const [generatingExcel, setGeneratingExcel] = useState(false);

  const currentYear = new Date().getFullYear();
  const yearOptions = [];
  for (let y = currentYear; y >= currentYear - 3; y--) {
    yearOptions.push(String(y));
  }

  useEffect(() => {
    fetchEmployees();
  }, []);

  const fetchEmployees = async () => {
    try {
      const response = await axios.get(`${API}/admin/clock/all-employees`);
      setEmployees(response.data.employees || []);
    } catch (error) {
      toast.error('Failed to load employees');
    } finally {
      setLoadingEmployees(false);
    }
  };

  const selectedEmployee = employees.find(e => e.id === selectedEmployeeId);
  const monthLabel = MONTHS.find(m => m.value === selectedMonth)?.label || '';

  const handleView = async () => {
    if (!selectedEmployeeId) {
      toast.error('Please select an employee');
      return;
    }
    setLoadingView(true);
    setViewData(null);
    try {
      const monthParam = `${selectedYear}-${selectedMonth}`;
      const response = await axios.get(`${API}/admin/clock/employee-history/${selectedEmployeeId}?month=${monthParam}`);
      const history = response.data.history || [];
      history.sort((a, b) => (a.date || '').localeCompare(b.date || ''));
      setViewData(history);
      if (history.length === 0) toast.info('No records found for this month');
    } catch (error) {
      const msg = error.response?.data?.detail || error.message || 'Unknown error';
      toast.error(`Failed to load history: ${msg}`);
    } finally {
      setLoadingView(false);
    }
  };

  const handlePrintReport = () => {
    if (!viewData || viewData.length === 0 || !selectedEmployee) {
      toast.error('No data to print. Press View first.');
      return;
    }
    let totalHours = 0;
    let daysWorked = 0;
    viewData.forEach(entry => {
      if (entry.clock_in && entry.clock_out) {
        const diff = (new Date(entry.clock_out.timestamp) - new Date(entry.clock_in.timestamp)) / (1000 * 60 * 60);
        totalHours += diff;
        daysWorked++;
      }
    });

    const entriesHtml = viewData.map(entry => {
      const clockIn = entry.clock_in ? formatUK(entry.clock_in.timestamp) : '--';
      const clockOut = entry.clock_out ? formatUK(entry.clock_out.timestamp) : '--';
      let hours = '--';
      if (entry.clock_in && entry.clock_out) {
        const diff = (new Date(entry.clock_out.timestamp) - new Date(entry.clock_in.timestamp)) / (1000 * 60 * 60);
        hours = diff.toFixed(2) + ' hrs';
      }
      const dateStr = format(parseISO(entry.date), 'EEEE, dd MMMM yyyy');
      const scheduled = entry.shift_start && entry.shift_end ? `${entry.shift_start} - ${entry.shift_end}` : '';
      const location = entry.site_name || '';
      const role = entry.clock_in?.shift_role || '';

      return `
        <div style="border:1px solid #e5e7eb;border-radius:6px;padding:14px;margin-bottom:12px;page-break-inside:avoid;">
          <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:8px;">
            <div>
              <strong style="font-size:14px;">${dateStr}</strong>
              ${scheduled ? `<span style="color:#6b7280;font-size:12px;margin-left:12px;">Scheduled: ${scheduled}</span>` : ''}
            </div>
            <div style="display:flex;gap:8px;">
              ${role ? `<span style="background:#dbeafe;padding:2px 8px;border-radius:4px;font-size:11px;color:#1e40af;">${role}</span>` : ''}
              ${location ? `<span style="background:#f3f4f6;padding:2px 8px;border-radius:4px;font-size:11px;color:#4b5563;">${location}</span>` : ''}
            </div>
          </div>
          <table style="width:100%;font-size:13px;">
            <tr>
              <td style="padding:4px 0;"><span style="color:#16a34a;font-weight:600;">Clock In:</span> ${clockIn}</td>
              <td style="padding:4px 0;"><span style="color:#dc2626;font-weight:600;">Clock Out:</span> ${clockOut}</td>
              <td style="padding:4px 0;"><strong>Hours:</strong> ${hours}</td>
            </tr>
          </table>
        </div>`;
    }).join('');

    const html = `<!DOCTYPE html>
<html><head><meta charset="utf-8"><title>San Security - ${selectedEmployee.name} - ${monthLabel} ${selectedYear}</title>
<style>
  @media print { body { margin: 0; } .no-print { display: none !important; } @page { margin: 15mm; } }
  body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; color: #111; background: #fff; padding: 20px; }
  .header { text-align: center; border-bottom: 3px solid #FACC15; padding-bottom: 16px; margin-bottom: 20px; }
  .header h1 { color: #FACC15; font-size: 28px; letter-spacing: 3px; margin: 0; background: #18181b; padding: 12px; }
  .header h2 { margin: 10px 0 4px; font-size: 18px; }
  .header p { margin: 2px 0; color: #6b7280; font-size: 13px; }
  .summary { display: flex; gap: 20px; justify-content: center; margin: 16px 0 24px; }
  .summary-box { background: #f9fafb; border: 1px solid #e5e7eb; border-radius: 6px; padding: 12px 24px; text-align: center; }
  .summary-box .num { font-size: 22px; font-weight: 700; }
  .summary-box .label { font-size: 11px; color: #6b7280; text-transform: uppercase; letter-spacing: 1px; }
  .print-btn { position: fixed; top: 20px; right: 20px; background: #FACC15; color: #18181b; border: none; padding: 10px 24px; font-size: 14px; font-weight: 600; border-radius: 6px; cursor: pointer; z-index:100; }
  .totals-section { margin-top: 30px; border: 2px solid #18181b; border-radius: 8px; padding: 20px; page-break-inside: avoid; }
  .totals-section h3 { margin: 0 0 14px 0; font-size: 16px; text-transform: uppercase; letter-spacing: 2px; border-bottom: 2px solid #FACC15; padding-bottom: 8px; }
  .totals-table { width: 100%; border-collapse: collapse; font-size: 13px; }
  .totals-table td { padding: 8px 12px; border-bottom: 1px solid #e5e7eb; }
  .totals-highlight { background: #FACC15; color: #18181b; padding: 12px 20px; border-radius: 6px; text-align: center; margin-top: 14px; }
  .totals-highlight .big { font-size: 28px; font-weight: 800; }
  .totals-highlight .small { font-size: 12px; text-transform: uppercase; letter-spacing: 1px; }
  .footer { text-align: center; color: #9ca3af; font-size: 11px; margin-top: 24px; padding-top: 12px; border-top: 1px solid #e5e7eb; }
</style></head><body>
<button class="print-btn no-print" onclick="window.print()">Print / Save PDF</button>
<div class="header">
  <h1>SAN SECURITY</h1>
  <h2>Monthly Clock Report</h2>
  <p><strong>Employee:</strong> ${selectedEmployee.name}</p>
  <p><strong>Month:</strong> ${monthLabel} ${selectedYear}</p>
</div>
<div class="summary">
  <div class="summary-box"><div class="num">${viewData.length}</div><div class="label">Total Days</div></div>
  <div class="summary-box"><div class="num">${daysWorked}</div><div class="label">Days Worked</div></div>
  <div class="summary-box"><div class="num">${totalHours.toFixed(2)}</div><div class="label">Total Hours</div></div>
</div>
${entriesHtml}
<div class="totals-section">
  <h3>Total Work Hours</h3>
  <table class="totals-table">
    <tr><td>Total Scheduled Days</td><td style="text-align:right;font-weight:700;">${viewData.length}</td></tr>
    <tr><td>Days Clocked In</td><td style="text-align:right;font-weight:700;">${daysWorked}</td></tr>
    <tr><td>Total Hours Worked</td><td style="text-align:right;font-weight:700;">${totalHours.toFixed(2)} hrs</td></tr>
    <tr><td>Average Hours Per Day</td><td style="text-align:right;font-weight:700;">${daysWorked > 0 ? (totalHours / daysWorked).toFixed(2) : '0.00'} hrs</td></tr>
  </table>
  <div class="totals-highlight">
    <div class="big">${totalHours.toFixed(2)}</div>
    <div class="small">Total Hours Worked</div>
  </div>
</div>
<div class="footer">Generated: ${format(new Date(), 'dd/MM/yyyy HH:mm')} - San Security</div>
</body></html>`;

    const printWindow = window.open('', '_blank');
    printWindow.document.write(html);
    printWindow.document.close();
  };

  // Unified per-staff Excel — SAME layout as the Admin Clock History export
  // (photos, GPS links, totals, hours-by-role). Rate/Pay columns are kept for
  // an identical look but intentionally left empty for supervisors.
  const handleDownloadExcel = async () => {
    if (!viewData || viewData.length === 0 || !selectedEmployee) {
      toast.error('No data to download. Press View first.');
      return;
    }
    setGeneratingExcel(true);
    try {
      const workbook = new ExcelJS.Workbook();
      workbook.creator = 'San Security';
      const worksheet = workbook.addWorksheet('Clock Report');
      worksheet.mergeCells('A1:O1');
      worksheet.getCell('A1').value = `SAN SECURITY - ${selectedEmployee.name} - ${monthLabel} ${selectedYear}`;
      worksheet.getCell('A1').font = { size: 14, bold: true };
      worksheet.getCell('A1').alignment = { horizontal: 'center' };
      worksheet.addRow([]);

      const headerRow = worksheet.addRow(['Date', 'Day', 'Scheduled', 'Clock In', 'Clock Out', 'Hours', 'Location', 'Role', 'Late In Reason', 'Late Out Reason', 'Notes', 'Clock In Photo', 'Clock Out Photo', 'Clock In GPS', 'Clock Out GPS', 'Pontaj Check', 'Rate (£)', 'Pay (£)']);
      headerRow.font = { bold: true, color: { argb: 'FFFFFFFF' } };
      headerRow.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF18181b' } };
      headerRow.alignment = { horizontal: 'center' };
      worksheet.columns = [
        { width: 14 }, { width: 12 }, { width: 14 },
        { width: 10 }, { width: 10 }, { width: 10 },
        { width: 22 }, { width: 22 },
        { width: 30 }, { width: 30 },
        { width: 30 },
        { width: 22 }, { width: 22 },
        { width: 16 }, { width: 16 },
        { width: 34 },
        { width: 10 }, { width: 12 },
      ];

      const parseHHMM = (t) => {
        if (t === null || t === undefined) return null;
        const s = String(t).trim();
        if (!s) return null;
        const m = /^(\d{1,2})[:.](\d{2})/.exec(s);
        if (!m) return null;
        const h = parseInt(m[1], 10);
        const mm = parseInt(m[2], 10);
        if (Number.isNaN(h) || Number.isNaN(mm) || h < 0 || h > 23 || mm < 0 || mm > 59) return null;
        return { h, m: mm };
      };

      // Fetch punch photos in ONE batched request (fallback to per-photo GETs).
      const photoIds = [];
      viewData.forEach((entry) => {
        if (entry.clock_in?.photo_url === 'has_photo' && entry.clock_in?.id) photoIds.push(entry.clock_in.id);
        if (entry.clock_out?.photo_url === 'has_photo' && entry.clock_out?.id) photoIds.push(entry.clock_out.id);
      });
      const photoMap = {};
      if (photoIds.length > 0) {
        try {
          const r = await axios.post(`${API}/admin/clock/photos/batch`, { ids: photoIds });
          Object.assign(photoMap, r.data?.photos || {});
        } catch (_e) {
          await Promise.all(photoIds.map(async (id) => {
            try {
              const r = await axios.get(`${API}/admin/clock/photo/${id}`);
              if (r.data?.photo_url && r.data.photo_url !== 'has_photo') photoMap[id] = r.data.photo_url;
            } catch (_e2) { /* skip */ }
          }));
        }
      }

      // Pontaj auto-verification is an admin-only endpoint; when unavailable
      // the column shows n/a and the summary row is skipped.
      let verifyDays = null;
      try {
        const vr = await axios.get(`${API}/admin/clock/verify/${selectedEmployee.id}`, { params: { month: `${selectedYear}-${selectedMonth}` } });
        verifyDays = vr.data?.days || {};
      } catch (_e) { verifyDays = null; }
      let verifyOk = 0; let verifyDiff = 0;

      let totalHours = 0;
      const hoursByRole = {};
      const scheduledHoursByRole = {};
      for (let i = 0; i < viewData.length; i++) {
        const entry = viewData[i];
        const clockIn = entry.clock_in ? formatUK(entry.clock_in.timestamp) : '--';
        const clockOut = entry.clock_out ? formatUK(entry.clock_out.timestamp) : '--';
        let hours = 0;
        let hoursStr = '--';
        if (entry.clock_in && entry.clock_out) {
          const diff = (new Date(entry.clock_out.timestamp) - new Date(entry.clock_in.timestamp)) / 3600000;
          if (!Number.isNaN(diff) && diff >= 0 && diff <= 24) {
            hours = diff;
            hoursStr = hours.toFixed(2);
            totalHours += hours;
            const roleKey = entry.clock_in?.shift_role || entry.shift_role || 'Unspecified';
            hoursByRole[roleKey] = (hoursByRole[roleKey] || 0) + hours;
          } else {
            hoursStr = 'INVALID';
          }
        }
        {
          const s = parseHHMM(entry.shift_start);
          const e = parseHHMM(entry.shift_end);
          if (s && e) {
            let mins = (e.h * 60 + e.m) - (s.h * 60 + s.m);
            if (mins <= 0) mins += 24 * 60;
            const schedRoleKey = entry.shift_role || entry.clock_in?.shift_role || 'Unspecified';
            scheduledHoursByRole[schedRoleKey] = (scheduledHoursByRole[schedRoleKey] || 0) + mins / 60;
          }
        }

        const clockInPhotoData = entry.clock_in?.id && photoMap[entry.clock_in.id];
        const clockOutPhotoData = entry.clock_out?.id && photoMap[entry.clock_out.id];
        const clockInPhotoCell = clockInPhotoData ? 'See image' : (entry.clock_in?.photo_url === 'has_photo' ? 'Photo in app' : '--');
        const clockOutPhotoCell = clockOutPhotoData ? 'See image' : (entry.clock_out?.photo_url === 'has_photo' ? 'Photo in app' : '--');

        const gpsLink = (e) => (e && typeof e.latitude === 'number' && typeof e.longitude === 'number')
          ? `https://maps.google.com/?q=${e.latitude},${e.longitude}`
          : null;
        const inGps = gpsLink(entry.clock_in);
        const outGps = gpsLink(entry.clock_out);
        const lateInReason = entry.clock_in?.late_reason || '';
        const lateOutReason = entry.clock_out?.late_reason || '';

        let pontajCheck = 'n/a';
        if (verifyDays && (entry.clock_in || entry.clock_out)) {
          const v = verifyDays[entry.date];
          const inHH = entry.clock_in ? formatUK(entry.clock_in.timestamp).slice(-5) : '';
          const outHH = entry.clock_out ? formatUK(entry.clock_out.timestamp).slice(-5) : '';
          if (v && v.clock_in === inHH && (v.clock_out || '') === outHH
            && (hoursStr === '--' || Math.abs((v.hours || 0) - hours) < 0.02)) {
            pontajCheck = '✓ verified';
            verifyOk += 1;
          } else if (v) {
            pontajCheck = `⚠ DIFF — pontaj: ${v.clock_in || '--'}–${v.clock_out || '--'} (${(v.hours || 0).toFixed(2)}h)`;
            verifyDiff += 1;
          } else {
            pontajCheck = '⚠ no pontaj found for this day';
            verifyDiff += 1;
          }
        }

        const row = worksheet.addRow([
          format(parseISO(entry.date), 'dd/MM/yyyy'),
          format(parseISO(entry.date), 'EEEE'),
          entry.shift_start && entry.shift_end ? `${entry.shift_start} - ${entry.shift_end}` : '--',
          clockIn,
          clockOut,
          hoursStr,
          entry.site_name || '--',
          entry.clock_in?.shift_role || '--',
          lateInReason || '--',
          lateOutReason || '--',
          entry.clock_in?.notes || '',
          clockInPhotoCell,
          clockOutPhotoCell,
          inGps ? 'View map' : (entry.clock_in ? 'No GPS' : '--'),
          outGps ? 'View map' : (entry.clock_out ? 'No GPS' : '--'),
          pontajCheck,
          '--',
          '--',
        ]);
        if (pontajCheck.startsWith('⚠')) {
          row.getCell(16).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFECACA' } };
          row.getCell(16).font = { bold: true, color: { argb: 'FF991B1B' } };
        } else if (pontajCheck.startsWith('✓')) {
          row.getCell(16).font = { color: { argb: 'FF16A34A' } };
        }
        if (inGps) {
          const c = row.getCell(14);
          c.value = { text: 'View map', hyperlink: inGps };
          c.font = { color: { argb: 'FF2563EB' }, underline: true };
        }
        if (outGps) {
          const c = row.getCell(15);
          c.value = { text: 'View map', hyperlink: outGps };
          c.font = { color: { argb: 'FF2563EB' }, underline: true };
        }

        const rowIdx = row.number;
        let needsTallRow = false;
        if (clockInPhotoData) {
          try {
            const m = /^data:image\/(\w+);base64,(.*)$/.exec(clockInPhotoData);
            const ext = (m?.[1] || 'jpeg').toLowerCase();
            const b64 = m?.[2] || clockInPhotoData;
            const imgId = workbook.addImage({ base64: b64, extension: ext === 'jpg' ? 'jpeg' : ext });
            worksheet.addImage(imgId, { tl: { col: 11, row: rowIdx - 1 }, ext: { width: 100, height: 75 } });
            needsTallRow = true;
          } catch (_e) { /* keep text only */ }
        }
        if (clockOutPhotoData) {
          try {
            const m = /^data:image\/(\w+);base64,(.*)$/.exec(clockOutPhotoData);
            const ext = (m?.[1] || 'jpeg').toLowerCase();
            const b64 = m?.[2] || clockOutPhotoData;
            const imgId = workbook.addImage({ base64: b64, extension: ext === 'jpg' ? 'jpeg' : ext });
            worksheet.addImage(imgId, { tl: { col: 12, row: rowIdx - 1 }, ext: { width: 100, height: 75 } });
            needsTallRow = true;
          } catch (_e) { /* keep text only */ }
        }
        if (needsTallRow) row.height = 58;
        if (lateInReason)  row.getCell(9).fill  = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFEF3C7' } };
        if (lateOutReason) row.getCell(10).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFEDD5' } };
        if (i % 2 === 0)   row.fill            = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF5F5F5' } };
      }

      const totalScheduledExcel = Object.values(scheduledHoursByRole).reduce((s, h) => s + h, 0);
      worksheet.addRow([]);
      const totalRow = worksheet.addRow([
        'TOTAL', '',
        totalScheduledExcel > 0 ? `${totalScheduledExcel.toFixed(2)} hrs scheduled` : '',
        '', '', totalHours.toFixed(2),
        '', '', '', '', '', '', '', '', '', '', '', '',
      ]);
      totalRow.font = { bold: true };
      totalRow.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFACC15' } };
      if (verifyDays) {
        const verifyRow = worksheet.addRow([
          `AUTO-VERIFICATION: ${verifyOk + verifyDiff} rows checked against recorded punches · ${verifyOk} verified · ${verifyDiff} flagged`,
        ]);
        verifyRow.font = { bold: true, italic: true, color: { argb: verifyDiff > 0 ? 'FF991B1B' : 'FF16A34A' } };
      }

      const empNameUpper = (selectedEmployee.name || '').toUpperCase();
      const breakdown = Object.entries(hoursByRole).sort((a, b) => b[1] - a[1]);
      if (breakdown.length > 0) {
        worksheet.addRow([]);
        const headerByRole = worksheet.addRow(['HOURS BY ROLE (WORKED)', empNameUpper, '', '', '', '', '', '', '', '', '', '', '']);
        headerByRole.font = { bold: true, size: 12 };
        headerByRole.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE5E7EB' } };
        breakdown.forEach(([role, hrs]) => {
          const r = worksheet.addRow([role, '', '', '', '', hrs.toFixed(2), '', '', '', '', '', '', '']);
          r.getCell(1).font = { bold: true };
          r.getCell(6).font = { bold: true };
        });
      }

      const schedBreakdown = Object.entries(scheduledHoursByRole).sort((a, b) => b[1] - a[1]);
      if (schedBreakdown.length > 0) {
        worksheet.addRow([]);
        const headerSched = worksheet.addRow(['HOURS BY ROLE (SCHEDULED)', empNameUpper, '', '', '', '', '', '', '', '', '', '', '']);
        headerSched.font = { bold: true, size: 12 };
        headerSched.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFDBEAFE' } };
        let totalSched = 0;
        schedBreakdown.forEach(([role, hrs]) => {
          totalSched += hrs;
          const r = worksheet.addRow([role, '', '', '', '', hrs.toFixed(2), '', '', '', '', '', '', '']);
          r.getCell(1).font = { bold: true };
          r.getCell(6).font = { bold: true };
        });
        const totalSchedRow = worksheet.addRow(['TOTAL SCHEDULED', '', '', '', '', totalSched.toFixed(2), '', '', '', '', '', '', '']);
        totalSchedRow.font = { bold: true };
        totalSchedRow.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFBFDBFE' } };
      }

      worksheet.eachRow((row, rowNumber) => {
        if (rowNumber >= 3) {
          row.eachCell((cell) => {
            cell.border = {
              top: { style: 'thin', color: { argb: 'FFcccccc' } },
              left: { style: 'thin', color: { argb: 'FFcccccc' } },
              bottom: { style: 'thin', color: { argb: 'FFcccccc' } },
              right: { style: 'thin', color: { argb: 'FFcccccc' } },
            };
          });
        }
      });

      const buffer = await workbook.xlsx.writeBuffer();
      saveAs(new Blob([buffer]), `${selectedEmployee.name.replace(/\s+/g, '_')}_${monthLabel}_${selectedYear}.xlsx`);
      toast.success('Excel downloaded');
    } catch (error) {
      toast.error('Failed to generate Excel');
    } finally {
      setGeneratingExcel(false);
    }
  };

  let totalHours = 0;
  let daysWorked = 0;
  if (viewData) {
    viewData.forEach(entry => {
      if (entry.clock_in && entry.clock_out) {
        const diff = (new Date(entry.clock_out.timestamp) - new Date(entry.clock_in.timestamp)) / (1000 * 60 * 60);
        totalHours += diff;
        daysWorked++;
      }
    });
  }

  if (loadingEmployees) {
    return (
      <SupervisorLayout>
        <div className="p-6 flex items-center justify-center min-h-screen">
          <div className="animate-pulse text-muted-foreground">Loading...</div>
        </div>
      </SupervisorLayout>
    );
  }

  return (
    <SupervisorLayout>
      <div className="p-4 sm:p-6 space-y-6 min-h-screen">
        <div className="border-b border-border pb-4">
          <h1 className="text-xl sm:text-2xl font-barlow tracking-widest text-foreground flex items-center gap-3">
            <Clock className="w-5 h-5 sm:w-6 sm:h-6 text-primary" strokeWidth={1.5} />
            CLOCK HISTORY
          </h1>
          <p className="text-xs sm:text-sm text-muted-foreground mt-1">
            View employee clock in/out records by month
          </p>
        </div>

        <Card className="bg-card border-border rounded-sm" data-testid="supervisor-clock-history-selection">
          <CardContent className="p-4">
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 items-end">
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Employee</Label>
                <Select value={selectedEmployeeId} onValueChange={setSelectedEmployeeId}>
                  <SelectTrigger className="bg-background border-border" data-testid="sup-select-employee">
                    <SelectValue placeholder="Select employee..." />
                  </SelectTrigger>
                  <SelectContent className="bg-card border-border max-h-60">
                    {employees.map(emp => (
                      <SelectItem key={emp.id} value={emp.id}>{emp.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Month</Label>
                <Select value={selectedMonth} onValueChange={setSelectedMonth}>
                  <SelectTrigger className="bg-background border-border" data-testid="sup-select-month">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-card border-border">
                    {MONTHS.map(m => (
                      <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Year</Label>
                <Select value={selectedYear} onValueChange={setSelectedYear}>
                  <SelectTrigger className="bg-background border-border" data-testid="sup-select-year">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-card border-border">
                    {yearOptions.map(y => (
                      <SelectItem key={y} value={y}>{y}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button
                onClick={handleView}
                disabled={!selectedEmployeeId || loadingView}
                className="bg-primary text-primary-foreground hover:bg-primary/90 h-10"
                data-testid="sup-view-btn"
              >
                <Eye className="w-4 h-4 mr-2" />
                {loadingView ? 'Loading...' : 'View'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {viewData && (
          <>
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <User className="w-4 h-4 text-primary" />
                  <span className="font-medium text-foreground">{selectedEmployee?.name}</span>
                  <Badge variant="outline" className="text-xs">{monthLabel} {selectedYear}</Badge>
                </div>
                <div className="flex items-center gap-3 text-sm text-muted-foreground">
                  <span>{viewData.length} days</span>
                  <span className="text-muted-foreground">|</span>
                  <span>{daysWorked} worked</span>
                  <span className="text-muted-foreground">|</span>
                  <span className="text-primary font-medium">{totalHours.toFixed(1)} hrs</span>
                </div>
              </div>
              {viewData.length > 0 && (
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={handlePrintReport} className="border-primary text-primary hover:bg-primary/10" data-testid="sup-print-report-btn">
                    <Printer className="w-4 h-4 mr-1" /> Print Report
                  </Button>
                  <Button variant="outline" size="sm" onClick={handleDownloadExcel} disabled={generatingExcel} className="border-green-600 text-green-500 hover:bg-green-900/20" data-testid="sup-download-excel-btn">
                    <FileArchive className="w-4 h-4 mr-1" /> {generatingExcel ? 'Generating...' : 'Download Excel'}
                  </Button>
                </div>
              )}
            </div>

            {viewData.length > 0 ? (
              <div className="space-y-2">
                {viewData.map((entry, idx) => {
                  let hoursWorked = '--';
                  if (entry.clock_in && entry.clock_out) {
                    const diff = (new Date(entry.clock_out.timestamp) - new Date(entry.clock_in.timestamp)) / (1000 * 60 * 60);
                    hoursWorked = diff.toFixed(2) + ' hrs';
                  }
                  return (
                    <Card key={idx} className="bg-card border-border rounded-sm">
                      <CardContent className="p-3 sm:p-4">
                        <div className="flex items-center justify-between gap-2 flex-wrap">
                          <div className="min-w-[180px]">
                            <p className="text-sm font-medium text-foreground">
                              {format(parseISO(entry.date), 'EEE, dd MMM yyyy')}
                            </p>
                            {entry.shift_start && entry.shift_end && (
                              <p className="text-xs text-muted-foreground">Scheduled: {entry.shift_start} - {entry.shift_end}</p>
                            )}
                          </div>
                          {entry.site_name && (
                            <Badge variant="outline" className="text-xs flex items-center gap-1 shrink-0">
                              <MapPin className="w-3 h-3" /> {entry.site_name}
                            </Badge>
                          )}
                          {entry.clock_in?.shift_role && (
                            <Badge className="text-xs bg-blue-600/10 text-blue-400 border-blue-900 flex items-center gap-1">
                              <Briefcase className="w-3 h-3" /> {entry.clock_in.shift_role}
                            </Badge>
                          )}
                          <div className="flex items-center gap-1.5">
                            <ArrowUpRight className="w-3.5 h-3.5 text-green-400" />
                            <span className="text-xs text-muted-foreground">In:</span>
                            <span className="text-sm font-mono text-foreground">
                              {entry.clock_in ? formatUK(entry.clock_in.timestamp) : '--:--'}
                            </span>
                          </div>
                          <div className="flex items-center gap-1.5">
                            <ArrowDownRight className="w-3.5 h-3.5 text-red-400" />
                            <span className="text-xs text-muted-foreground">Out:</span>
                            <span className="text-sm font-mono text-foreground">
                              {entry.clock_out ? formatUK(entry.clock_out.timestamp) : '--:--'}
                            </span>
                          </div>
                          <div className="flex items-center gap-1.5">
                            <Clock className="w-3.5 h-3.5 text-primary" />
                            <span className="text-sm font-medium text-primary">{hoursWorked}</span>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            ) : (
              <Card className="bg-card border-border rounded-sm">
                <CardContent className="p-8 text-center text-muted-foreground">
                  <Calendar className="w-10 h-10 mx-auto mb-3 opacity-50" />
                  <p className="text-sm">No records for {monthLabel} {selectedYear}</p>
                </CardContent>
              </Card>
            )}
          </>
        )}

        {!viewData && !loadingView && (
          <Card className="bg-card border-border rounded-sm">
            <CardContent className="p-12 text-center text-muted-foreground">
              <Clock className="w-12 h-12 mx-auto mb-4 opacity-30" />
              <p className="text-sm">Select an employee and month, then press <strong>View</strong></p>
            </CardContent>
          </Card>
        )}
      </div>
    </SupervisorLayout>
  );
}
